// =============================================================================
// Optim TURPE V2 — LA QUALITÉ DES MESURES EN JOURS (DEV-2175, I5 et m4 de la
// revue du 25/09/2026).
//
// Règle de Mathieu : « x jours de données » est un COMPTE de jours, et le trou
// doit être lisible. L'en-tête ne disait qu'un pourcentage. Ce module lit ce que
// le moteur sert déjà (`qualite_donnees`) et le met en phrases ; il ne calcule
// aucun compte lui-même, et une absence reste une absence (jamais un 0).
// =============================================================================

import { formateurFr } from "@modules/courtage/utils/optim-turpe-v2.nombre"
import {
    lireChiffre,
    lireStructureChoisie,
} from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import { reservesSurLeurValeur } from "@modules/courtage/utils/optim-turpe-v2.entetes"
import { reservesDeLaConclusion } from "@modules/courtage/utils/optim-turpe-v2.recommandation"
import {
    formatPctMesure,
    LIBELLE_VERDICT_CLASSE,
    libelleClasseHoraire,
} from "@modules/courtage/utils/optim-turpe-v2.libelles"
import type {
    OptimTurpeV2Etude,
    OptimTurpeV2QualiteDonnees,
} from "@modules/courtage/types/optim-turpe-v2"

function entier(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur)
        ? Math.round(valeur)
        : null
}

/**
 * « 353 jours mesurés sur 365, plus long trou 9 j », ou « …, sans trou ».
 * `null` quand le moteur ne sert pas les deux comptes : rien n'est écrit.
 *
 * Rejeu du 20/10/2026 (A13) : `plus_long_trou_jours` est compté par le moteur
 * au pas de dix minutes (courtage `data_quality.py`, DEV-1910), en jours à
 * deux décimales. Un trou de moins d'un jour se dit en heures, comme la
 * mention servie (courtage `reserves_servies._jours_mesures`) : 0,58 j est
 * « 14 h », jamais « 1 j », arrondi qui contredisait la mention.
 */
export function libelleJoursMesures(
    qualite: OptimTurpeV2QualiteDonnees | null | undefined
): string | null {
    const avec = entier(qualite?.jours_avec_donnee)
    const fenetre = entier(qualite?.jours_fenetre)
    if (avec === null || fenetre === null) return null
    const base = `${avec} jours mesurés sur ${fenetre}`
    const brut = qualite?.plus_long_trou_jours
    if (typeof brut !== "number" || !Number.isFinite(brut)) return base
    if (brut <= 0) return `${base}, sans trou`
    if (brut < 1) {
        const heures = Math.max(1, Math.round(brut * 24))
        return `${base}, plus long trou ${heures} h`
    }
    return `${base}, plus long trou ${Math.round(brut)} j`
}

/**
 * La mention servie est-elle le décompte des jours mesurés (« 310 jours
 * mesurés sur 310, plus long trou 14 h ») ? Il redit alors la ligne
 * « Mesures », qui l'écrit déjà (A13).
 */
export function estLeDecompteDesJours(texte: string): boolean {
    return /^\d+ jours mesurés sur \d+/.test(texte.trim())
}

/**
 * Une ligne par classe de la formule, avec son verdict sur la période.
 * `null` quand le moteur ne sert pas le registre (ou n'a pas su compter).
 */
export function lignesCouvertureParClasse(
    etude: OptimTurpeV2Etude
): Array<{ classe: string; texte: string; verdict: string | null }> | null {
    const registre = etude.qualite_donnees?.couverture_par_classe
    if (!Array.isArray(registre) || registre.length === 0) return null
    return registre
        .filter((ligne) => typeof ligne?.classe === "string")
        .map((ligne) => {
            const classe = String(ligne.classe)
            const verdict = ligne.verdict ? String(ligne.verdict) : null
            const verdictLu = verdict
                ? (LIBELLE_VERDICT_CLASSE[verdict] ?? verdict)
                : null
            const observes = entier(ligne.jours_observes)
            const offerts = entier(ligne.jours_offerts)
            // DEV-2311 : la classe en clair dans la phrase, jamais son code seul.
            const nom = libelleClasseHoraire(classe)
            const enClair = nom.charAt(0).toUpperCase() + nom.slice(1)
            // Une classe que la période n'offre pas n'a pas de compte à lire.
            if (
                verdict === "hors-fenetre" ||
                offerts === 0 ||
                offerts === null
            ) {
                return {
                    classe,
                    verdict,
                    texte: `${enClair} : ${verdictLu ?? "non précisée"}`,
                }
            }
            const compte =
                observes === null
                    ? `${offerts} jours offerts`
                    : `${observes} jours observés sur ${offerts}`
            return {
                classe,
                verdict,
                texte: verdictLu
                    ? `${enClair} : ${compte}, ${verdictLu}`
                    : `${enClair} : ${compte}`,
            }
        })
}

export type EtatJour = "complet" | "partiel" | "absent" | "inconnu"

/**
 * Les états que la légende de la frise nomme (revue témoin du 15/10/2026,
 * D24) : les trois états de mesure, puis « Non compté » quand un jour de la
 * frise porte cet état ; une case grise n'est jamais absente de la légende.
 */
export function etatsDeLaLegende(
    frise: ReadonlyArray<{ etat: EtatJour }> | null
): EtatJour[] {
    const etats: EtatJour[] = ["complet", "partiel", "absent"]
    if (frise?.some((jour) => jour.etat === "inconnu")) etats.push("inconnu")
    return etats
}

/**
 * La frise jour par jour (`qualite_donnees.trame_par_jour`), triée par date
 * (D24 : l'ordre servi n'est pas garanti), ou `null`.
 */
export function friseParJour(
    etude: OptimTurpeV2Etude
): Array<{ date: string; etat: EtatJour; titre: string }> | null {
    const trame = etude.qualite_donnees?.trame_par_jour
    if (!Array.isArray(trame) || trame.length === 0) return null
    return trame
        .filter((jour) => typeof jour?.date === "string")
        .sort((a, b) => String(a.date).localeCompare(String(b.date)))
        .map((jour) => {
            const attendus = entier(jour.pas_attendus)
            const mesures = entier(jour.pas_mesures)
            const date = String(jour.date)
            const [a, m, j] = date.slice(0, 10).split("-")
            const dateFr = j && m && a ? `${j}/${m}/${a}` : date
            if (attendus === null || attendus === 0 || mesures === null) {
                return {
                    date,
                    etat: "inconnu" as const,
                    titre: `${dateFr} : mesures non comptées`,
                }
            }
            const etat: EtatJour =
                mesures >= attendus
                    ? "complet"
                    : mesures > 0
                      ? "partiel"
                      : "absent"
            return {
                date,
                etat,
                titre: `${dateFr} : ${mesures} relevé${mesures >= 2 ? "s" : ""} sur ${attendus}`,
            }
        })
}

type JourDeFrise = { date: string; etat: EtatJour; titre: string }

const MOIS_FR = [
    "janvier",
    "février",
    "mars",
    "avril",
    "mai",
    "juin",
    "juillet",
    "août",
    "septembre",
    "octobre",
    "novembre",
    "décembre",
]

/**
 * La frise découpée en une ligne par mois, chacune avec son libellé
 * (« mars 2026 ») : série UX du témoin (15/10/2026, 2.3 point 7). L'ordre est
 * celui de la frise, déjà triée par date.
 */
export function friseParMois(
    frise: JourDeFrise[]
): { mois: string; libelle: string; jours: JourDeFrise[] }[] {
    const parMois = new Map<string, JourDeFrise[]>()
    for (const jour of frise) {
        const mois = jour.date.slice(0, 7)
        parMois.set(mois, [...(parMois.get(mois) ?? []), jour])
    }
    return [...parMois.entries()].map(([mois, jours]) => {
        const m = /^(\d{4})-(\d{2})$/.exec(mois)
        const libelle = m
            ? `${MOIS_FR[Number(m[2]) - 1] ?? m[2]} ${m[1]}`
            : mois
        return { mois, libelle, jours }
    })
}

/**
 * La synthèse de la frise, en jours comptés : « 364 jours complets, 1 partiel,
 * 0 sans mesure ». Ce sont des comptes de jours connus (un 0 y est un compte,
 * pas une donnée absente) ; les jours non comptés ne s'écrivent que s'il y en
 * a.
 */
export function syntheseDeLaFrise(frise: JourDeFrise[]): string {
    const n = (etat: EtatJour) => frise.filter((j) => j.etat === etat).length
    const complets = n("complet")
    const partiels = n("partiel")
    const absents = n("absent")
    const inconnus = n("inconnu")
    const parties = [
        `${complets} jour${complets > 1 ? "s" : ""} complet${complets > 1 ? "s" : ""}`,
        `${partiels} partiel${partiels > 1 ? "s" : ""}`,
        `${absents} sans mesure`,
    ]
    if (inconnus > 0)
        parties.push(`${inconnus} non compté${inconnus > 1 ? "s" : ""}`)
    return parties.join(", ")
}

// =============================================================================
// Revue du 25/09/2026, lot Front-F (courtage#256).
// =============================================================================

/** Un pourcentage de mesure à une décimale : la règle unique de la page (A2). */
export { formatPctMesure }

/** « 2026-07-12 » → « 12/07 ». */
function jourMois(iso: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
    return m ? `${m[3]}/${m[2]}` : iso
}

function listeFrancaise(elements: string[]): string {
    if (elements.length <= 1) return elements.join("")
    return `${elements.slice(0, -1).join(", ")} et ${elements[elements.length - 1]}`
}

/** Nombre de dates citées au plus ; au-delà, « dont » (même règle que le moteur). */
const JOURS_CITES_MAX = 7

/**
 * « 2 jours reconstitués, le 12/07 et le 25/09 » (DEV-2181), lu dans
 * `reserves[donnees-completees].valeurs.jours_reconstitues`. `null` quand le
 * champ n'est pas servi ou qu'il est vide : on n'écrit jamais « 0 jour ».
 */
export function libelleJoursReconstitues(
    etude: OptimTurpeV2Etude
): string | null {
    const reserve = (etude.reserves ?? []).find(
        (r) => r.code === "donnees-completees"
    )
    const jours = reserve?.valeurs?.jours_reconstitues
    if (!Array.isArray(jours)) return null
    const dates = jours.filter((j): j is string => typeof j === "string")
    if (dates.length === 0) return null
    const n = dates.length
    const compte = n === 1 ? "1 jour reconstitué" : `${n} jours reconstitués`
    const cites = dates
        .slice(0, JOURS_CITES_MAX)
        .map((j) => `le ${jourMois(j)}`)
    const dont = n > JOURS_CITES_MAX ? "dont " : ""
    return `${compte}, ${dont}${listeFrancaise(cites)}`
}

/** Le code de la réserve par classe sur l'année (DEV-2181, M3). */
export const RESERVE_CLASSE_SUR_L_ANNEE =
    "classe-temporelle-sous-observee-sur-lannee"

/**
 * La réserve « sous-observée sur l'année » d'UNE classe, avec son levier, pour
 * être lue sur la ligne de la classe dans la preuve. `null` sinon.
 */
export function reserveDeClasseSurLAnnee(
    etude: OptimTurpeV2Etude,
    classe: string
): { label: string; levier: string | null } | null {
    const reserve = (etude.reserves ?? []).find(
        (r) =>
            r.code === RESERVE_CLASSE_SUR_L_ANNEE &&
            r.valeurs?.classe === classe
    )
    if (!reserve) return null
    const label = reserve.label.trim().replace(/\.$/, "")
    const brut = reserve.levier?.libelle?.trim()
    const levier = brut
        ? `Levier : ${brut.charAt(0).toLowerCase()}${brut.slice(1)}`
        : null
    return { label: `${label}.`, levier }
}

/** Les classes dont la réserve sur l'année se lit dans la preuve. */
export function classesAvecReserveDansLaPreuve(
    etude: OptimTurpeV2Etude
): Set<string> {
    const lignes = lignesCouvertureParClasse(etude) ?? []
    return new Set(
        lignes
            .map((ligne) => ligne.classe)
            .filter((classe) => reserveDeClasseSurLAnnee(etude, classe))
    )
}

function heures(valeur: number): string {
    return `${formateurFr({ maximumFractionDigits: 1 }).format(valeur)}\u00a0h`
}

/**
 * L'exposition aux dépassements comptés sur des mesures reconstituées
 * (DEV-2188, I5). Lue sous `exposition_depassement_recommandee` quand le moteur
 * la sert (courtage#256) : c'est la puissance que le client va demander. Sinon
 * l'ancienne clé, qui se lit contre la structure actuelle. `null` quand les
 * heures ne sont pas mesurées, ou qu'aucune heure n'est exposée.
 */
export function lireExpositionDepassement(
    etude: OptimTurpeV2Etude
): { texte: string; contre: "proposee" | "actuelle" } | null {
    const qualite = etude.qualite_donnees
    const neuve = qualite?.exposition_depassement_recommandee
    const expo = neuve ?? qualite?.exposition_depassement
    if (!expo) return null
    const contre = neuve ? "proposee" : "actuelle"
    const possibles = expo.heures_fabriquees_possibles
    const certaines = expo.heures_fabriquees_certaines
    if (typeof possibles !== "number" || !Number.isFinite(possibles)) {
        return null
    }
    if (possibles <= 0) return null
    const structure =
        contre === "proposee"
            ? lireStructureChoisie(etude)
                ? "vos puissances"
                : "les puissances recommandées"
            : "le contrat actuel"
    const debut = `Dépassements comptés sur des mesures estimées, contre ${structure}`
    const fourchette =
        typeof certaines === "number" &&
        Number.isFinite(certaines) &&
        certaines !== possibles
            ? `entre ${heures(certaines)} et ${heures(possibles)}`
            : `jusqu'à ${heures(possibles)}`
    return { texte: `${debut} : ${fourchette}`, contre }
}

// =============================================================================
// Maquette finale du 15/10/2026 (DEV-2404) : « Points à vérifier » (M8 de
// Mélodie), une rangée du sommaire. Les mesures et les réserves y sont réunies ;
// un seul point se lit en orange, celui qui touche la conclusion.
// =============================================================================

/**
 * Un point à vérifier : sa valeur, sa phrase, et s'il est LE point orange.
 * `reserve` : les seuls textes servis des réserves (sans la ligne des
 * mesures), que l'alerte de la carte reprend ; `court` : la première
 * proposition du texte qui fragilise la conclusion, pour le résumé de la
 * rangée (`null` hors du point orange).
 */
export type PointAVerifier = {
    libelle: string
    texte: string
    orange: boolean
    reserve: string | null
    court: string | null
    /**
     * Revue 2 en production (15/10/2026, B2) : le constat en UNE ligne
     * (« libellé : constat » tient en 110 caractères), lu au repos.
     * Absent : la ligne dit `texte`.
     */
    constat?: string
    /**
     * Le paragraphe complet, derrière « Voir le détail », quand il dit plus
     * que le constat ; `null` sinon (rien à déplier).
     */
    detail?: string | null
}

/** Une ligne de « Points à vérifier », libellé compris (B2). */
export const LONGUEUR_MAX_POINT = 110

/**
 * Le constat court d'un texte servi, pour tenir « libellé : constat » en une
 * ligne (B2) : la première phrase si elle tient ; sinon ses propositions
 * (coupées aux « ; », « : » et virgules) tant qu'elles tiennent ; en dernier
 * recours les premiers mots, suivis de « … ». Le texte entier reste au détail.
 */
export function constatCourt(texte: string, budget: number): string {
    const propre = texte.trim()
    if (propre.length <= budget) return propre
    const phrase =
        /^(.+?[.!?])(?=\s+[\p{Lu}«]|\s*$)/su.exec(propre)?.[1] ?? propre
    if (phrase.length <= budget) return phrase
    const morceaux = phrase.split(/(?<=[;:,])\s/)
    let retenu = ""
    for (const morceau of morceaux) {
        const suivant = retenu ? `${retenu} ${morceau}` : morceau
        if (suivant.length > budget) break
        retenu = suivant
    }
    if (retenu) return retenu.replace(/[\s;:,]+$/, "")
    const mots = phrase.split(/\s+/)
    let debut = ""
    for (const mot of mots) {
        const suivant = debut ? `${debut} ${mot}` : mot
        if (suivant.length > budget - 1) break
        debut = suivant
    }
    return `${debut}…`
}

/**
 * Sur une étude non chiffrée, le point « Puissances recommandées » est hors
 * sujet (revue 2, I45) : l'étude ne conclut pas sur les puissances. Seul ce
 * point se retire, par son libellé ; jamais un point d'après les mots de son
 * texte servi, pour qu'une vraie réserve (et le point orange) ne disparaisse
 * pas en silence (relecture sceptique de la revue 2).
 */
const POINT_HORS_SUJET_SANS_CHIFFRE = "Puissances recommandées"

/** L'étude affichée ne chiffre pas l'économie (état « montant non servi »). */
function estNonChiffree(etude: OptimTurpeV2Etude): boolean {
    const chiffre = lireChiffre(etude)
    if (chiffre.estScenarioLibre) return false
    return (
        chiffre.montantServi === false || chiffre.etat === "montant_non_servi"
    )
}

/**
 * L'effet d'une valeur sur la conclusion, du plus fort au plus faible (revue
 * sceptique du 15/10/2026) : le raccordement et les puissances d'abord, la
 * puissance réactive (elle fait la puissance apparente), la période, puis le
 * montant et ses parts ; les mesures en dernier, elles disent surtout ce qui
 * a été mesuré.
 */
const EFFET_SUR_LA_CONCLUSION = [
    "Contrat actuel",
    "Puissances recommandées",
    "Vos puissances",
    "Puissance réactive",
    "Période",
    "Montant",
    "Dépassements",
    "Demande au fournisseur",
    "Autres remarques",
    "Mesures",
]

/** Les portées servies qui disent qu'une réserve touche la conclusion (Q20). */
const PORTEES_DE_CONCLUSION = new Set(["recommandation", "montant"])

/**
 * Le poids d'une réserve pour le choix du point orange : 2 quand sa portée
 * servie touche la recommandation ou le montant ; 1 quand la portée n'est pas
 * servie (étude ancienne, ou code sans portée) : la page ne la déclasse pas
 * sans savoir, SAUF sur « Mesures », qui ne passe en orange que si le moteur
 * dit qu'elle touche la conclusion ; 0 sinon (`entree`, ou une portée servie
 * qui ne touche pas la conclusion).
 */
function poidsDeLaReserve(valeur: string, portee: string | null): number {
    if (portee !== null && PORTEES_DE_CONCLUSION.has(portee)) return 2
    if (portee === null && valeur !== "Mesures") return 1
    return 0
}

/** « Puissance réactive partiellement absente : … » → « Puissance réactive partiellement absente ». */
function premiereProposition(texte: string): string {
    const coupe = texte.split(/\s[:;]\s|\.(?:\s|$)/)[0] ?? texte
    return coupe.trim().replace(/[.\s]+$/, "")
}

/**
 * M9 de Mélodie : jamais « extrapolation » à l'écran ; une courbe comblée par
 * endroits se dit « en partie complétée (relevés isolés) ». Le texte servi est
 * gardé, seul ce mot est remplacé.
 */
export function sansExtrapolation(texte: string): string {
    return texte
        .replace(/\s*par extrapolation/gi, " (relevés isolés)")
        .replace(
            /\bextrapol(ées|és|ée|é)(?![a-zà-ÿ])/gi,
            (_m, fin: string) => `en partie complét${fin} (relevés isolés)`
        )
        .replace(/\bextrapolation\b/gi, "complétion (relevés isolés)")
}

/**
 * La ligne « Mesures » : les jours mesurés et leur plus long trou, le pas des
 * relevés, les jours reconstitués ; la couverture mesurée à défaut des jours.
 * `null` quand rien n'est servi (jamais un 0, jamais un mot de remplacement).
 */
export function ligneDesMesures(etude: OptimTurpeV2Etude): string | null {
    const jours = libelleJoursMesures(etude.qualite_donnees)
    const couverture = formatPctMesure(
        etude.qualite_donnees?.couverture_mesuree_pct
    )
    const base =
        jours ??
        (couverture ? `Courbe de charge mesurée à ${couverture}` : null)
    if (!base) return null
    const pas = etude.qualite_donnees?.pas_natif_minutes
    return [
        base,
        typeof pas === "number" ? `relevés de ${pas} minutes` : null,
        libelleJoursReconstitues(etude),
    ]
        .filter(Boolean)
        .join(", ")
}

/** La ligne « Mesures » sans les jours reconstitués, quand la réserve les dit. */
function ligneDesMesuresSansReconstitues(
    etude: OptimTurpeV2Etude
): string | null {
    const complete = ligneDesMesures(etude)
    const reconstitues = libelleJoursReconstitues(etude)
    if (!complete || !reconstitues) return complete
    return complete.replace(`, ${reconstitues}`, "")
}

/**
 * Les points à vérifier de l'étude affichée, dans l'ordre de lecture de la
 * page : les réserves servies, regroupées par valeur touchée
 * (`reservesSurLeurValeur`), la ligne « Mesures » fondue avec sa réserve.
 * Le seul point orange est celui qui fragilise le plus la conclusion
 * (`poidsDeLaReserve`, puis `EFFET_SUR_LA_CONCLUSION`), jamais le premier dans
 * l'ordre de la page ; il se lit d'abord, les autres en gris.
 */
export function pointsAVerifier(etude: OptimTurpeV2Etude): PointAVerifier[] {
    const parValeur = reservesSurLeurValeur(
        reservesDeLaConclusion(etude, { preuveSousLesYeux: false }),
        { structureChoisie: lireStructureChoisie(etude) }
    )
    type Brouillon = PointAVerifier & { poids: number; fragile: string | null }
    const points: Brouillon[] = parValeur.map((ligne) => {
        const portees = ligne.textes.map((_t, i) => ligne.portees?.[i] ?? null)
        const poids = portees.map((p) => poidsDeLaReserve(ligne.valeur, p))
        const max = Math.max(0, ...poids)
        const fragile = max > 0 ? ligne.textes[poids.indexOf(max)] : null
        const reserve = sansExtrapolation(ligne.textes.join(" "))
        return {
            libelle: ligne.valeur,
            texte: reserve,
            reserve,
            orange: false,
            court: null,
            poids: max,
            fragile: fragile ? sansExtrapolation(fragile) : null,
        }
    })
    const pointMesures = points.find((p) => p.libelle === "Mesures")
    // Revue 2 (I08) : la réserve servie qui dit déjà les jours reconstitués
    // ne se double pas de la même mention sur la ligne des mesures.
    const mesures =
        pointMesures && /reconstitu/i.test(pointMesures.texte)
            ? ligneDesMesuresSansReconstitues(etude)
            : ligneDesMesures(etude)
    if (mesures && pointMesures)
        pointMesures.texte = `${mesures}. ${pointMesures.texte}`
    else if (mesures)
        points.push({
            libelle: "Mesures",
            texte: mesures,
            reserve: null,
            orange: false,
            court: null,
            poids: 0,
            fragile: null,
        })
    // Revue 2 (I45) : une étude non chiffrée ne dit pas de puissances
    // recommandées.
    if (estNonChiffree(etude)) {
        const garde = points.filter(
            (p) => p.libelle !== POINT_HORS_SUJET_SANS_CHIFFRE
        )
        points.splice(0, points.length, ...garde)
    }
    const rang = (p: Brouillon) => {
        const r = EFFET_SUR_LA_CONCLUSION.indexOf(p.libelle)
        return r === -1 ? EFFET_SUR_LA_CONCLUSION.length : r
    }
    const elu = points
        .filter((p) => p.poids > 0)
        .sort((a, b) => b.poids - a.poids || rang(a) - rang(b))[0]
    const ordonnes = elu ? [elu, ...points.filter((p) => p !== elu)] : points
    return ordonnes.map(({ libelle, texte, reserve, fragile }) => {
        const constat = constatCourt(
            texte,
            LONGUEUR_MAX_POINT - libelle.length - 3
        )
        return {
            libelle,
            texte,
            constat,
            detail: constat === texte.trim() ? null : texte,
            reserve,
            orange: elu !== undefined && elu.libelle === libelle,
            court:
                elu !== undefined && elu.libelle === libelle && fragile
                    ? premiereProposition(fragile)
                    : null,
        }
    })
}

/**
 * Ce qui manque aux mesures, en mots courts (revue 2, I47) : les mois de la
 * frise sans aucun jour mesuré (« 3 mois sans mesure ») ; à défaut de frise,
 * les jours de la fenêtre sans donnée (« 112 jours sans mesure ») ; `null`
 * quand rien ne manque ou que le moteur ne sert pas les comptes.
 */
export function manqueDeMesures(etude: OptimTurpeV2Etude): string | null {
    const frise = friseParJour(etude)
    if (frise) {
        const mois = friseParMois(frise).filter((m) =>
            m.jours.every((j) => j.etat === "absent")
        ).length
        if (mois > 0) return `${mois} mois sans mesure`
    }
    const avec = entier(etude.qualite_donnees?.jours_avec_donnee)
    const fenetre = entier(etude.qualite_donnees?.jours_fenetre)
    if (avec === null || fenetre === null || fenetre <= avec) return null
    const manque = fenetre - avec
    return `${manque} jour${manque > 1 ? "s" : ""} sans mesure`
}

/**
 * Le résumé de la rangée « Points à vérifier » (revue 2, B2 et B11), défini
 * dès un point : le compte, puis, sur une étude non chiffrée, ce qui manque
 * aux mesures ; sinon le point orange en quelques mots ; sinon l'état des
 * mesures (« Mesures complètes » quand tous les jours de la fenêtre sont
 * mesurés). Jamais la phrase du moteur sur la formule recommandée.
 */
export function resumeDesPointsAVerifier(
    points: PointAVerifier[],
    etude: OptimTurpeV2Etude
): string | null {
    if (points.length === 0) return null
    const compte = points.length === 1 ? "1 point" : `${points.length} points`
    const orange = points.find((p) => p.orange)
    const avec = entier(etude.qualite_donnees?.jours_avec_donnee)
    const fenetre = entier(etude.qualite_donnees?.jours_fenetre)
    const complet = avec !== null && fenetre !== null && avec >= fenetre
    const phrase = estNonChiffree(etude)
        ? manqueDeMesures(etude)
        : (orange?.court ??
          (points.some((p) => p.libelle === "Mesures")
              ? (manqueDeMesures(etude) ??
                (complet ? "Mesures complètes" : null))
              : null))
    return [compte, phrase].filter(Boolean).join(" · ")
}
